/**
 * ClusterResultsPage - WriterZen-style Cluster Results View
 *
 * Displays all keyword clusters from a topical map with:
 * - Statistics cards (total clusters, keywords, volume, etc.)
 * - Filterable/sortable cluster table
 * - Intent badges, difficulty indicators
 * - Bulk actions for status updates
 * - "Create Article" button to send clusters to blog creation
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Table,
  Tag,
  Space,
  Typography,
  Button,
  Input,
  Select,
  Tooltip,
  Progress,
  Statistic,
  Row,
  Col,
  Empty,
  Badge,
  Dropdown,
  Modal,
} from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import type { MenuProps } from 'antd';
import {
  ArrowLeft,
  Search,
  Filter,
  Download,
  Star,
  StarOff,
  FileText,
  Target,
  TrendingUp,
  Layers,
  BarChart3,
  ChevronDown,
  ExternalLink,
  Sparkles,
  Eye,
} from 'lucide-react';
import { useTenantStore } from '@/stores/useTenantStore';
import {
  useTopicalMap,
  useTopicalMapStatistics,
  useClusters,
  useUpdateCluster,
  useBulkUpdateClusterStatus,
  useExportMapCsv,
} from '@/hooks/useContentPlannerAPI';
import {
  ClusterSummary,
  ClusterStatus,
  SearchIntent,
  ClusterFilters,
  INTENT_LABELS,
  INTENT_COLORS,
  INTENT_FULL_LABELS,
  STATUS_LABELS,
  STATUS_COLORS,
  getDifficultyLevel,
  DIFFICULTY_LABELS,
  DIFFICULTY_COLORS,
} from '@/types/contentPlanner';
import { toast } from 'sonner';
import ClusterDetailModal from './ClusterDetailModal';
import CreateArticlesModal from '@/components/content-planner/CreateArticlesModal';
import loadingAnimation from '@/assets/loading-animation.webp';

const { Title, Text } = Typography;

const ClusterResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const { mapId } = useParams<{ mapId: string }>();
  const { currentTenant } = useTenantStore();
  const queryClient = useQueryClient();

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ClusterFilters>({});
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);
  const [showCreateArticlesModal, setShowCreateArticlesModal] = useState(false);
  const [singleClusterForModal, setSingleClusterForModal] = useState<ClusterSummary | null>(null);

  // API hooks - useTopicalMap has built-in polling while processing
  const { data: mapData, isLoading: mapLoading } = useTopicalMap(currentTenant, mapId || null);
  const { data: statistics } = useTopicalMapStatistics(currentTenant, mapId || null);
  const { data: clusters = [], isLoading: clustersLoading } = useClusters(
    currentTenant,
    mapId || null,
    filters
  );
  const updateClusterMutation = useUpdateCluster();
  const bulkUpdateMutation = useBulkUpdateClusterStatus();
  const exportCsvMutation = useExportMapCsv();

  // Check if map is still processing
  const isProcessing = mapData && !['completed', 'failed'].includes(mapData.status);

  // Filter clusters by search term
  const filteredClusters = useMemo(() => {
    if (!searchTerm) return clusters;
    const term = searchTerm.toLowerCase();
    return clusters.filter(
      (cluster) =>
        cluster.name.toLowerCase().includes(term) ||
        cluster.keywords_preview?.some((kw) => kw.toLowerCase().includes(term))
    );
  }, [clusters, searchTerm]);

  // Toggle individual row selection (used by extended checkbox click area)
  const toggleRowSelection = useCallback((clusterId: string, selected: boolean) => {
    setSelectedRowKeys(prev => {
      if (selected) {
        return [...prev, clusterId];
      } else {
        return prev.filter(key => key !== clusterId);
      }
    });
  }, []);

  const handleStarCluster = async (clusterId: string, isStarred: boolean) => {
    if (!currentTenant || !mapId) return;

    try {
      await updateClusterMutation.mutateAsync({
        tenantId: currentTenant,
        mapId,
        clusterId,
        data: { is_starred: !isStarred },
      });
    } catch (error) {
      toast.error('Failed to update cluster');
    }
  };

  const handleStatusChange = async (clusterId: string, status: ClusterStatus) => {
    if (!currentTenant || !mapId) return;

    try {
      await updateClusterMutation.mutateAsync({
        tenantId: currentTenant,
        mapId,
        clusterId,
        data: { status },
      });
      toast.success(`Status updated to ${STATUS_LABELS[status]}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleBulkStatusChange = async (status: ClusterStatus) => {
    if (!currentTenant || !mapId || selectedRowKeys.length === 0) return;

    try {
      await bulkUpdateMutation.mutateAsync({
        tenantId: currentTenant,
        mapId,
        data: {
          cluster_ids: selectedRowKeys as string[],
          status,
        },
      });
      toast.success(`Updated ${selectedRowKeys.length} clusters to ${STATUS_LABELS[status]}`);
      setSelectedRowKeys([]);
    } catch (error) {
      toast.error('Failed to update clusters');
    }
  };

  const handleExportCsv = async () => {
    if (!currentTenant || !mapId) return;

    try {
      await exportCsvMutation.mutateAsync({
        tenantId: currentTenant,
        mapId,
      });
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleCreateArticle = (cluster: ClusterSummary) => {
    // Open modal with single cluster - "Fast-Track" UX pattern
    // User stays on page, selects template inline, creates content directly
    setSingleClusterForModal(cluster);
    setShowCreateArticlesModal(true);
  };

  const handleBulkCreateArticles = () => {
    if (selectedRowKeys.length === 0) return;
    setShowCreateArticlesModal(true);
  };

  // Get clusters for the modal - handles both single and bulk selection
  const clustersForModal = useMemo(() => {
    // Single cluster takes priority (from "Create" button on a row)
    if (singleClusterForModal) {
      return [singleClusterForModal];
    }
    // Bulk selection (from checkboxes + "Create articles" button)
    return clusters.filter((c) => selectedRowKeys.includes(c.id));
  }, [clusters, selectedRowKeys, singleClusterForModal]);

  // Bulk actions menu
  const bulkActionsMenu: MenuProps['items'] = [
    {
      key: 'approve',
      label: 'Mark as Approved',
      onClick: () => handleBulkStatusChange('approved'),
    },
    {
      key: 'in_progress',
      label: 'Mark as In Progress',
      onClick: () => handleBulkStatusChange('in_progress'),
    },
    {
      key: 'create_articles',
      label: (
        <Space>
          <Sparkles size={14} />
          <span>Create Articles</span>
        </Space>
      ),
      onClick: handleBulkCreateArticles,
    },
  ];

  const columns: ColumnsType<ClusterSummary> = [
    {
      title: '',
      key: 'star',
      width: 40,
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={
            record.is_starred ? (
              <Star size={16} fill="#faad14" color="#faad14" />
            ) : (
              <StarOff size={16} className="text-muted-foreground" />
            )
          }
          onClick={(e) => {
            e.stopPropagation();
            handleStarCluster(record.id, !!record.is_starred);
          }}
        />
      ),
    },
    {
      title: 'Cluster Name',
      dataIndex: 'name',
      key: 'name',
      width: 280,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string, record) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ cursor: 'pointer' }} onClick={() => setSelectedClusterId(record.id)}>
            {name}
          </Text>
          {record.keywords_preview && record.keywords_preview.length > 0 && (
            <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.3 }}>
              {record.keywords_preview.slice(0, 3).join(' · ')}
              {record.keyword_count > 3 && ` +${record.keyword_count - 3}`}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Intent',
      dataIndex: 'primary_intent',
      key: 'intent',
      width: 80,
      filters: [
        { text: 'Informational', value: 'informational' },
        { text: 'Commercial', value: 'commercial_investigation' },
        { text: 'Transactional', value: 'transactional' },
        { text: 'Navigational', value: 'navigational' },
      ],
      onFilter: (value, record) => record.primary_intent === value,
      render: (intent: SearchIntent) => (
        <Tooltip title={INTENT_FULL_LABELS[intent]}>
          <Tag color={INTENT_COLORS[intent]}>{INTENT_LABELS[intent]}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'KWs',
      dataIndex: 'keyword_count',
      key: 'keyword_count',
      width: 70,
      align: 'center',
      sorter: (a, b) => a.keyword_count - b.keyword_count,
      render: (count: number) => (
        <Tooltip title={`${count} keywords in this cluster`}>
          <Badge count={count} style={{ backgroundColor: '#1890ff' }} />
        </Tooltip>
      ),
    },
    {
      title: 'MSV',
      dataIndex: 'total_volume',
      key: 'total_volume',
      width: 100,
      align: 'right',
      sorter: (a, b) => a.total_volume - b.total_volume,
      defaultSortOrder: 'descend',
      render: (volume: number) => (
        <Tooltip title="Total Monthly Search Volume">
          <Text strong>{volume.toLocaleString()}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'KD',
      dataIndex: 'avg_difficulty',
      key: 'difficulty',
      width: 100,
      sorter: (a, b) => a.avg_difficulty - b.avg_difficulty,
      render: (kd: number) => {
        const level = getDifficultyLevel(kd);
        return (
          <Tooltip title={`${DIFFICULTY_LABELS[level]} (${Math.round(kd)})`}>
            <Space size={4}>
              <Progress
                percent={kd}
                size="small"
                showInfo={false}
                strokeColor={
                  kd <= 30 ? '#52c41a' : kd <= 50 ? '#faad14' : kd <= 70 ? '#fa8c16' : '#f5222d'
                }
                style={{ width: 50 }}
              />
              <Text type="secondary">{Math.round(kd)}</Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'CPC',
      dataIndex: 'avg_cpc',
      key: 'cpc',
      width: 80,
      align: 'right',
      sorter: (a, b) => a.avg_cpc - b.avg_cpc,
      render: (cpc: number) => (
        <Text type="secondary">${cpc?.toFixed(2) || '0.00'}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: [
        { text: 'New', value: 'new' },
        { text: 'Approved', value: 'approved' },
        { text: 'In Progress', value: 'in_progress' },
        { text: 'Draft Ready', value: 'draft_ready' },
        { text: 'Published', value: 'published' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status: ClusterStatus, record) => (
        <Select
          size="small"
          value={status}
          onChange={(value) => handleStatusChange(record.id, value)}
          style={{ width: 110 }}
          onClick={(e) => e.stopPropagation()}
          options={Object.entries(STATUS_LABELS).map(([value, label]) => ({
            value,
            label: <Tag color={STATUS_COLORS[value as ClusterStatus]}>{label}</Tag>,
          }))}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<FileText size={16} />}
              onClick={() => setSelectedClusterId(record.id)}
            />
          </Tooltip>
          {record.content_id ? (
            <Tooltip title="View Article">
              <Button
                type="primary"
                size="small"
                icon={<Eye size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/editor/blog/${record.content_id}`);
                }}
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              >
                View
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Create Article">
              <Button
                type="primary"
                size="small"
                icon={<Sparkles size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateArticle(record);
                }}
              >
                Create
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Row selection with extended click area - clicking anywhere in checkbox cell toggles selection
  const rowSelection: TableProps<ClusterSummary>['rowSelection'] = useMemo(
    () => ({
      selectedRowKeys,
      onChange: (keys) => setSelectedRowKeys(keys),
      columnWidth: 48,
      onCell: (record: ClusterSummary) => ({
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          const isSelected = selectedRowKeys.includes(record.id);
          toggleRowSelection(record.id, !isSelected);
        },
        style: { cursor: 'pointer' },
      }),
    }),
    [selectedRowKeys, toggleRowSelection]
  );

  if (mapLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <img src={loadingAnimation} alt="Loading" className="dark:invert w-40 h-auto" />
      </div>
    );
  }

  if (!mapData) {
    return (
      <div className="p-6">
        <Empty description="Research session not found" />
        <div className="text-center mt-4">
          <Button onClick={() => navigate('/content/planner')}>
            Back to Content Planner
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Processing Banner */}
      {isProcessing && (
        <Card className="bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-4">
            <img src={loadingAnimation} alt="Loading" className="dark:invert w-8 h-8" />
            <div className="flex-1">
              <Text strong>Processing keyword research...</Text>
              <div className="text-sm text-muted-foreground">
                {mapData?.current_stage || 'Starting processing'}
              </div>
            </div>
            <Progress
              percent={mapData?.progress_percentage || 0}
              style={{ width: 200 }}
              status="active"
            />
          </div>
        </Card>
      )}

      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-4">
          <Button
            icon={<ArrowLeft size={16} />}
            onClick={() => navigate('/content/planner')}
          >
            Back
          </Button>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              {mapData.seed_keyword}
            </Title>
            <Space size={8}>
              <Text type="secondary">Keyword Research Results</Text>
              {mapData.target_url && (
                <a
                  href={mapData.target_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 dark:text-blue-400 hover:text-blue-600"
                >
                  <Space size={4}>
                    <ExternalLink size={12} />
                    <span>{mapData.target_url}</span>
                  </Space>
                </a>
              )}
            </Space>
          </div>
        </div>
        <Space>
          <Button
            icon={<Download size={16} />}
            onClick={handleExportCsv}
            loading={exportCsvMutation.isPending}
          >
            Export CSV
          </Button>
        </Space>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <Row gutter={16}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Total Clusters"
                value={statistics.total_clusters}
                prefix={<Layers size={16} className="text-blue-500 dark:text-blue-400" />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Total Keywords"
                value={statistics.total_keywords}
                prefix={<Target size={16} className="text-green-500 dark:text-green-400" />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Total MSV"
                value={statistics.total_volume}
                prefix={<TrendingUp size={16} className="text-teal" />}
                formatter={(value) => value?.toLocaleString()}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Avg. Cluster Volume"
                value={Math.round(statistics.avg_cluster_volume)}
                prefix={<BarChart3 size={16} className="text-orange-500 dark:text-orange-400" />}
                formatter={(value) => value?.toLocaleString()}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters and Actions */}
      <Card size="small" className="shadow-sm">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <Space size={12}>
            <Input
              placeholder="Search clusters..."
              prefix={<Search size={16} className="text-muted-foreground" />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <Select
              placeholder="Filter by Intent"
              allowClear
              style={{ width: 160 }}
              onChange={(value) => setFilters((prev) => ({ ...prev, intent: value }))}
              options={[
                { value: 'informational', label: 'Informational' },
                { value: 'commercial_investigation', label: 'Commercial' },
                { value: 'transactional', label: 'Transactional' },
                { value: 'navigational', label: 'Navigational' },
              ]}
            />
            <Select
              placeholder="Filter by Status"
              allowClear
              style={{ width: 140 }}
              onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
              options={Object.entries(STATUS_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
            />
          </Space>

          {selectedRowKeys.length > 0 && (
            <Space>
              <Text type="secondary">{selectedRowKeys.length} selected</Text>
              <Button
                type="primary"
                icon={<Sparkles size={14} />}
                onClick={handleBulkCreateArticles}
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              >
                + Create articles
              </Button>
              <Dropdown menu={{ items: bulkActionsMenu }} trigger={['click']}>
                <Button>
                  <Space>
                    More Actions
                    <ChevronDown size={14} />
                  </Space>
                </Button>
              </Dropdown>
            </Space>
          )}
        </div>
      </Card>

      {/* Clusters Table */}
      <Card className="shadow-sm">
        <Table
          columns={columns}
          dataSource={filteredClusters}
          rowKey="id"
          rowSelection={rowSelection}
          loading={clustersLoading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `${total} clusters`,
          }}
          onRow={(record) => ({
            onClick: () => setSelectedClusterId(record.id),
            style: { cursor: 'pointer' },
          })}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Cluster Detail Modal */}
      <ClusterDetailModal
        tenantId={currentTenant}
        mapId={mapId || null}
        clusterId={selectedClusterId}
        onClose={() => setSelectedClusterId(null)}
        onCreateArticle={handleCreateArticle}
      />

      {/* Create Articles Modal */}
      <CreateArticlesModal
        open={showCreateArticlesModal}
        onClose={() => {
          setShowCreateArticlesModal(false);
          setSingleClusterForModal(null);  // Reset single cluster state
          setSelectedRowKeys([]);           // Clear bulk selection
        }}
        clusters={clustersForModal}
        mapId={mapId || ''}
      />
    </div>
  );
};

export default ClusterResultsPage;
